import { type FC, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../../../components/Button/Button'
import { SelectHostsModal } from './SelectHostsModal'
import { SectionHeader } from './HostsSection'
import { SingleHostSection } from './SingleHostSection'
import { RotatingHostsSection } from './RotatingHostsSection'
import { type EventTypeHost, type EventTypeHosts, HostMode, type SchedulingEventType } from '../../../../../models/Scheduling'

const MODE_COPY: Record<HostMode, { label: string; helper: string }> = {
  [HostMode.SINGLE]: { label: 'One host', helper: 'The same person takes every booking. Clients see their name.' },
  [HostMode.ROTATING]: { label: 'Rotating hosts', helper: 'Zocks assigns each booking to an available host. One host per meeting.' },
}

const HOST_MODES = [HostMode.SINGLE, HostMode.ROTATING]

interface HostsTabProps {
  eventType: SchedulingEventType
  hosts?: EventTypeHosts
  disabled: boolean
  onHostsChange: (patch: Partial<EventTypeHosts>) => void
  onRetry: () => void
}

interface ModeCardProps {
  mode: HostMode
  selected: boolean
  disabled: boolean
  onSelect: (mode: HostMode) => void
}

const ModeCard: FC<ModeCardProps> = ({ mode, selected, disabled, onSelect }) => {
  const id = `host-mode-${mode}`
  return (
    <label
      htmlFor={id}
      className={classNames(
        'd-flex align-items-start gap-3 p-3 rounded-3 w-100 h-100 border zocks-bg-color-white',
        selected ? 'zocks-border-color-primary' : 'zocks-border-color-gray-200',
        disabled ? undefined : 'cursor-pointer',
      )}
    >
      <Form.Check
        type='radio'
        id={id}
        name='host-mode'
        checked={selected}
        disabled={disabled}
        aria-label={MODE_COPY[mode].label}
        onChange={() => onSelect(mode)}
      />
      <span className='d-flex flex-column'>
        <span className='bold'>{MODE_COPY[mode].label}</span>
        <span className='paragraph-small zocks-text-gray-600'>{MODE_COPY[mode].helper}</span>
      </span>
    </label>
  )
}

export const HostsTab: FC<HostsTabProps> = ({ eventType, hosts, disabled, onHostsChange, onRetry }) => {
  const [isSelectOpen, setIsSelectOpen] = useState(false)

  if (!eventType.accountPageVisible) {
    return (
      <div className='d-flex align-items-center justify-content-center py-5 rounded-2 zocks-bg-color-gray-100 paragraph-medium zocks-text-gray-900 text-center'>
        Turn on the account booking page in the sidebar to choose who takes these bookings.
      </div>
    )
  }
  if (!hosts) {
    return (
      <div className='d-flex align-items-center justify-content-between gap-3 p-3 rounded-2 zocks-bg-color-warning-background paragraph-small'>
        <span className='d-flex align-items-center zocks-gap-0.5'>
          <i className='bi bi-exclamation-circle zocks-text-gray-600' aria-hidden='true' />
          Could not load the hosts.
        </span>
        <Button type='button' variant='grayButton' className='button-small' onClick={onRetry}>
          Retry
        </Button>
      </div>
    )
  }

  const handleSelectSave = (selected: EventTypeHost[]) => {
    setIsSelectOpen(false)
    onHostsChange({ hosts: selected })
  }

  const handleRemove = (userId: string) => {
    onHostsChange({ hosts: hosts.hosts.filter((host) => host.userId !== userId) })
  }

  const handleModeChange = (hostMode: HostMode) => {
    if (hostMode === hosts.hostMode) {
      return
    }
    onHostsChange({ hostMode, hosts: hostMode === HostMode.SINGLE ? hosts.hosts.slice(0, 1) : hosts.hosts })
  }

  const openSelect = () => {
    setIsSelectOpen(true)
  }

  return (
    <div className='d-flex flex-column gap-4'>
      <div className='d-flex flex-column gap-3'>
        <SectionHeader title='Host assignment' subtitle='Choose who takes bookings made on the account booking page.' />
        <Row role='radiogroup' aria-label='Host assignment' className='g-3'>
          {HOST_MODES.map((mode) => (
            <Col key={mode} xs={12} md={6}>
              <ModeCard mode={mode} selected={hosts.hostMode === mode} disabled={disabled} onSelect={handleModeChange} />
            </Col>
          ))}
        </Row>
      </div>
      {hosts.hostMode === HostMode.SINGLE ? (
        <SingleHostSection host={hosts.hosts[0]} disabled={disabled} onSelect={openSelect} />
      ) : (
        <RotatingHostsSection hosts={hosts.hosts} disabled={disabled} onAdd={openSelect} onRemove={handleRemove} />
      )}
      {isSelectOpen ? (
        <SelectHostsModal
          eventTypeId={eventType.id}
          hostMode={hosts.hostMode}
          selected={hosts.hosts}
          onSave={handleSelectSave}
          onCancel={() => setIsSelectOpen(false)}
        />
      ) : null}
    </div>
  )
}
